import { useContext } from "react";

import { AppStateContext } from "@/state/AppStateContext";

// The backend rolls the weekly window over at Monday 00:00 UTC; show that
// instant in the user's own timezone so the reset time is unambiguous.
const formatNextResetLocal = () => {
  const now = new Date();
  const reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  reset.setUTCDate(reset.getUTCDate() + ((1 - reset.getUTCDay() + 7) % 7 || 7));

  const weekday = reset.toLocaleDateString(undefined, { weekday: "long" });
  const time = reset
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .replace(/:00(?=\s*[AaPp][Mm])/, "")
    .replace(/\s*([AaPp])\.?[Mm]\.?$/, (_m, meridiem: string) => `${meridiem.toLowerCase()}m`);

  return `${weekday} ${time}`;
};

const UsageBar = () => {
  const appStateContext = useContext(AppStateContext);
  const usage = appStateContext?.state.weeklyUsage ?? null;

  // Usage is fetched on app load, so this only shows for the brief window
  // before that first response lands.
  if (!usage || usage.limit <= 0) {
    return (
      <div className="flex min-h-56 items-center justify-center">
        <span className="loading loading-ring text-primary"></span>
      </div>
    );
  }

  // Mirror the send gate, which blocks on used + in-flight reservations
  // reaching the limit. Ignore an initial reservation until the first usage
  // record arrives, so a new weekly window does not appear partially used.
  const consumed = usage.used + (usage.reserved ?? 0);
  const displayedConsumed = usage.used > 0 ? consumed : usage.used;
  const remainingTokens = Math.max(0, usage.limit - displayedConsumed);
  const remainingPct = Math.floor((remainingTokens / usage.limit) * 100);
  const usedPct = Math.floor((Math.min(displayedConsumed, usage.limit) / usage.limit) * 100);
  const usedLabel = `${usedPct}%`;
  const barColor = remainingPct <= 10 ? "bg-error" : remainingPct <= 25 ? "bg-warning" : "bg-primary";

  return (
    <div className="select-none rounded-xl border border-line p-5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm font-medium text-ink">Weekly usage limit</span>
        <span className="text-sm font-semibold tabular-nums text-ink">{usedLabel} used</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-inset">
        <div className={`h-full rounded-full ${barColor} transition-[width] duration-500 ease-out`} style={{ width: `${(Math.min(displayedConsumed, usage.limit) / usage.limit) * 100}%` }} />
      </div>
      <p className="mt-3 text-xs leading-relaxed text-ink-2">Measured across all of your chats. Resets {formatNextResetLocal()}.</p>
    </div>
  );
};

export default UsageBar;
